import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { todayStr } from '../utils/date';
import { VIRTUAL_REWARD_ECONOMY_ENABLED } from '../config/rewards';

export type CoinSource =
  | 'activity-completion'
  | 'enrichment-completion'
  | 'quest-completion'
  | 'streak-milestone'
  | 'badge-unlock'
  | 'shop-purchase'
  | 'event-reward'
  | 'movement-challenge'
  | 'story-builder'
  | 'pattern-studio'
  | 'weather-tracker'
  | 'park-trivia';

export interface CoinTransaction {
  id: string;
  childId: string;
  amount: number;
  source: CoinSource;
  description: string;
  timestamp: string;
}

interface CoinState {
  balances: Record<string, number>;
  transactions: CoinTransaction[];
  purchasedItems: Record<string, string[]>;
  equippedItems: Record<string, string[]>;
  lastLoginReward: Record<string, string>; // childId -> last reward date (YYYY-MM-DD)
  loginStreaks: Record<string, number>; // childId -> consecutive login days
  earnCoins: (childId: string, amount: number, source: CoinSource, description: string) => void;
  spendCoins: (childId: string, amount: number, itemId: string, description: string) => boolean;
  getBalance: (childId: string) => number;
  getTransactions: (childId: string) => CoinTransaction[];
  hasPurchased: (childId: string, itemId: string) => boolean;
  equipItem: (childId: string, itemId: string) => void;
  unequipItem: (childId: string, itemId: string) => void;
  transferItem: (fromChildId: string, toChildId: string, itemId: string) => void;
  claimLoginReward: (childId: string) => { coins: number; streak: number };
}

export const useCoinStore = create<CoinState>()(
  persist(
    (set, get) => ({
      balances: {},
      transactions: [],
      purchasedItems: {},
      equippedItems: {},
      lastLoginReward: {},
      loginStreaks: {},

      earnCoins: (childId, amount, source, description) => {
        if (!VIRTUAL_REWARD_ECONOMY_ENABLED) return;
        const tx: CoinTransaction = {
          id: crypto.randomUUID(),
          childId,
          amount,
          source,
          description,
          timestamp: new Date().toISOString(),
        };
        set(state => ({
          balances: {
            ...state.balances,
            [childId]: (state.balances[childId] || 0) + amount,
          },
          transactions: [...state.transactions, tx],
        }));
      },

      spendCoins: (childId, amount, itemId, description) => {
        if (!VIRTUAL_REWARD_ECONOMY_ENABLED) return false;
        const balance = get().balances[childId] || 0;
        if (balance < amount) return false;

        const tx: CoinTransaction = {
          id: crypto.randomUUID(),
          childId,
          amount: -amount,
          source: 'shop-purchase',
          description,
          timestamp: new Date().toISOString(),
        };
        set(state => ({
          balances: {
            ...state.balances,
            [childId]: (state.balances[childId] || 0) - amount,
          },
          transactions: [...state.transactions, tx],
          purchasedItems: {
            ...state.purchasedItems,
            [childId]: [...(state.purchasedItems[childId] || []), itemId],
          },
        }));
        return true;
      },

      getBalance: (childId) =>
        VIRTUAL_REWARD_ECONOMY_ENABLED ? get().balances[childId] || 0 : 0,

      getTransactions: (childId) =>
        VIRTUAL_REWARD_ECONOMY_ENABLED
          ? get().transactions.filter(t => t.childId === childId)
          : [],

      hasPurchased: (childId, itemId) =>
        VIRTUAL_REWARD_ECONOMY_ENABLED
          ? (get().purchasedItems[childId] || []).includes(itemId)
          : false,

      equipItem: (childId, itemId) => {
        if (!VIRTUAL_REWARD_ECONOMY_ENABLED) return;
        set(state => {
          let currentEquipped = [...(state.equippedItems[childId] || [])];
          // Only one theme can be active at a time
          if (itemId.startsWith('theme-')) {
            currentEquipped = currentEquipped.filter(id => !id.startsWith('theme-'));
          }
          return {
            equippedItems: {
              ...state.equippedItems,
              [childId]: [...new Set([...currentEquipped, itemId])],
            },
          };
        });
      },

      unequipItem: (childId, itemId) => {
        if (!VIRTUAL_REWARD_ECONOMY_ENABLED) return;
        set(state => ({
          equippedItems: {
            ...state.equippedItems,
            [childId]: (state.equippedItems[childId] || []).filter(id => id !== itemId),
          },
        }));
      },

      transferItem: (fromChildId, toChildId, itemId) => {
        if (!VIRTUAL_REWARD_ECONOMY_ENABLED) return;
        const fromItems = get().purchasedItems[fromChildId] || [];
        const idx = fromItems.indexOf(itemId);
        if (idx === -1) return;
        set(state => {
          const newFromItems = [...(state.purchasedItems[fromChildId] || [])];
          newFromItems.splice(idx, 1);
          return {
            purchasedItems: {
              ...state.purchasedItems,
              [fromChildId]: newFromItems,
              [toChildId]: [...(state.purchasedItems[toChildId] || []), itemId],
            },
            equippedItems: {
              ...state.equippedItems,
              [fromChildId]: (state.equippedItems[fromChildId] || []).filter(id => id !== itemId),
            },
          };
        });
      },

      claimLoginReward: (childId) => {
        if (!VIRTUAL_REWARD_ECONOMY_ENABLED) return { coins: 0, streak: 0 };
        const today = todayStr();
        const lastDate = get().lastLoginReward[childId];

        // Already claimed today
        if (lastDate === today) return { coins: 0, streak: 0 };

        // Calculate streak: if last reward was yesterday, increment; otherwise reset to 1
        const prevStreak = get().loginStreaks[childId] || 0;
        let newStreak = 1;
        if (lastDate) {
          const last = new Date(lastDate + 'T00:00:00');
          const now = new Date(today + 'T00:00:00');
          const diffDays = Math.round((now.getTime() - last.getTime()) / (1000 * 60 * 60 * 24));
          if (diffDays === 1) {
            newStreak = prevStreak + 1;
          }
        }

        // Base 5 coins + streak bonus (1 per streak day, max +10)
        const streakBonus = Math.min(newStreak - 1, 10);
        const totalCoins = 5 + streakBonus;

        // Update state
        set(state => ({
          lastLoginReward: { ...state.lastLoginReward, [childId]: today },
          loginStreaks: { ...state.loginStreaks, [childId]: newStreak },
        }));

        // Award coins via earnCoins
        get().earnCoins(childId, totalCoins, 'event-reward', `Daily login bonus (day ${newStreak})`);

        return { coins: totalCoins, streak: newStreak };
      },
    }),
    { name: 'homeschool-coins', version: 0, migrate: (state) => state }
  )
);
